import React from 'react';
import { Megaphone, MapPin, Calendar, ExternalLink } from 'lucide-react';
import { resolvePaidChannels, channelMeta, labelHref } from './paidChannels';
import AdCreativeViewer from './AdCreativeViewer';

const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null);

/**
 * The full paid advertising record for one affiliate, shown in the expanded row
 * of Affiliate Recruitment and Affiliate Directory.
 *
 * Exists because the inline marker beside the URL is deliberately just a letter.
 * Everything it used to spell out — the figure, the keyword, the channel — is
 * here, where a wide panel can hold it without pushing every row taller.
 *
 * Two states, and the difference matters more than any of the detail:
 *  - Advertising now: they are live, go and look at what they are bidding on.
 *  - Was advertising: the ads stopped, but they have run paid campaigns before.
 *    Still a far better prospect than an affiliate that never has, which is why
 *    the record is kept and shown rather than deleted with the traffic figure.
 *
 * Renders nothing when there is no paid activity at all, so the expanded row of
 * the ~99.5% of affiliates with none is unchanged.
 */
const PaidAdvertisingPanel = ({ row }) => {
  const paidTraffic = Number(row?.paid_traffic) || 0;
  const activity = row?.paid_activity || null;

  const liveChannels = resolvePaidChannels(row?.paid_channels, paidTraffic);
  const everChannels = activity?.channels || [];
  const pastChannels = everChannels.filter((c) => !liveChannels.includes(c));

  const isCurrent = liveChannels.length > 0 || paidTraffic > 0;
  const evidence = activity?.evidence || [];

  if (!isCurrent && pastChannels.length === 0 && evidence.length === 0) return null;

  const shownChannels = (liveChannels.length > 0 ? liveChannels : pastChannels).map(channelMeta).filter(Boolean);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Megaphone className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <h4 className="font-semibold text-gray-800 dark:text-slate-100">Paid Advertising</h4>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-lg p-3 shadow-sm dark:border dark:border-slate-700">
        {/* Status line — the one thing worth reading if nothing else is. */}
        <div className="flex flex-wrap items-center gap-2 pb-3 mb-3 border-b border-gray-100 dark:border-slate-700">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              isCurrent
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400'
            }`}
          >
            {isCurrent ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Advertising now
              </>
            ) : (
              'Was advertising'
            )}
          </span>

          {shownChannels.map((channel) => (
            <span
              key={channel.key}
              title={channel.where}
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${channel.chip}`}
            >
              <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ${channel.solid}`}>
                {channel.letter}
              </span>
              {channel.short}
            </span>
          ))}

          {isCurrent && paidTraffic > 0 && (
            <span className="text-xs text-gray-500 dark:text-slate-400">
              {paidTraffic.toLocaleString()} paid visits
            </span>
          )}

          {!isCurrent && activity?.last_seen_at && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-slate-400">
              <Calendar className="w-3 h-3" />
              last seen {fmtDate(activity.last_seen_at)}
            </span>
          )}
        </div>

        {evidence.length > 0 ? (
          <div className="space-y-1.5">
            {evidence.map((find, index) => {
              const findChannel = channelMeta(find.channel);
              // The advertiser field holds a Page name or the landing page the
              // operator pasted. Only the latter becomes a link.
              const href = labelHref(find.label);

              return (
                <div
                  key={`${find.label}-${index}`}
                  className="flex items-start gap-2 rounded-lg border border-gray-100 px-3 py-2 dark:border-slate-700"
                >
                  {findChannel && (
                    <span
                      title={findChannel.where}
                      className={`mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${findChannel.solid}`}
                    >
                      {findChannel.letter}
                    </span>
                  )}

                  <div className="flex-1 min-w-0">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex max-w-full items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
                        title={find.label}
                      >
                        <span className="truncate">{find.label}</span>
                        <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-60" />
                      </a>
                    ) : (
                      <span className="block truncate text-xs text-gray-700 dark:text-slate-300" title={find.label}>
                        {find.label}
                      </span>
                    )}

                    {/* Second line so the date on the right never gets squeezed
                        off, which is what happened when this all sat in one row. */}
                    {(find.platforms || find.ad_images || find.status === 'inactive') && (
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400 dark:text-slate-500">
                        {find.platforms && <span>{find.platforms.split(',').join(' · ')}</span>}
                        {/* The creative itself, captured when the find was logged — it
                            outlives the campaign, which an Ad Library link does not. */}
                        <AdCreativeViewer images={find.ad_images} label={find.label} compact />
                        {/* The ad's own state when it was logged — an inactive
                            ad is exactly the "they used to run this" case. */}
                        {find.status === 'inactive' && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 uppercase text-gray-500 dark:bg-slate-800 dark:text-slate-400">
                            ad inactive
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-shrink-0 flex-col items-end gap-0.5 text-[11px]">
                    {find.country && (
                      <span className="inline-flex items-center gap-1 text-gray-500 dark:text-slate-400">
                        <MapPin className="w-2.5 h-2.5" />
                        {find.country}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 whitespace-nowrap text-gray-400 dark:text-slate-500">
                      <Calendar className="w-2.5 h-2.5" />
                      {fmtDate(find.seen_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="py-2 text-center text-xs text-gray-400 dark:text-slate-500">
            {/* Paid traffic straight from SEMrush, with no find logged by the team. */}
            No individual ads recorded — this figure comes from SEMrush rather than a logged find.
          </p>
        )}
      </div>
    </div>
  );
};

export default PaidAdvertisingPanel;
